import styled from 'styled-components'
import { themeGet } from 'styled-system'
import { Field } from 'formik'
import {
  CalendarInput,
  CheckIcon,
  FormInputNumber,
  TooltipContentWrapper,
  WarningIcon,
} from 'secondstep-components'
import AdminTooltip from 'components/AdminTooltip'

// Fallbacks for test env where secondstep-components shim may not provide all exports
const CheckIconOrSpan = CheckIcon || 'span'
const WarningIconOrSpan = WarningIcon || 'span'
const CalendarInputOrInput = CalendarInput || 'input'
const FormInputNumberOrInput = FormInputNumber || 'input'

export const StyledCheckbox = styled.label`
  display: inline-block;
  position: relative;
  width: 16px;
  height: 16px;
  margin-right: 0.5rem;
  border: 2px solid ${themeGet('colors.blue')};
  border-radius: 4px;
  cursor: pointer;
  background-color: ${props =>
    props.checked ? themeGet('colors.blue')(props) : 'white'};
  transition: background-color 0.2s ease;

  input:checked + & {
    background-color: ${themeGet('colors.blue')};
  }
`

export const CheckboxWrapper = styled.div`
  display: flex;
  margin: 0.7rem 0 0;
`

export const CheckIconWrapper = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
  transition: opacity 0.2s ease;

  input:checked ~ ${StyledCheckbox} & {
    opacity: 1;
  }
`

export const HiddenCheckbox = styled(Field)`
  display: none;
`

export const AddGoalsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

export const AddGoalsCopy = styled.div`
  margin-bottom: 0.1875rem;
  font-size: 1rem;
  line-height: 1.25rem;
  color: ${themeGet('colors.darkGray')};
  font-family: ${themeGet('font.molde.regular')};
`

export const InputsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 1rem;
  justify-content: space-between;

  ${themeGet('breakpoints.mobile')} {
    flex-direction: column;
    gap: 1rem;
  }
`

export const DateWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

export const NumberWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  input {
    background: white;
  }
`

export const DateTitle = styled.div`
  font-size: 0.75rem;
  line-height: 1rem;
  color: ${themeGet('colors.darkGray')};
  font-family: ${themeGet('font.molde.medium')};
  margin-bottom: 0.25rem;
`

export const NumberTitle = styled.div`
  font-size: 0.75rem;
  line-height: 1rem;
  color: ${themeGet('colors.darkGray')};
  font-family: ${themeGet('font.molde.medium')};
  margin-bottom: 0.25rem;
`

export const OptionalGoalsSection = styled.section``

export const PreviousYearText = styled.div`
  font-size: 0.75rem;
  line-height: 1rem;
  color: ${themeGet('colors.darkGray')};
  font-family: ${themeGet('font.molde.regular')};
  margin-top: 0.5rem;
`

export const Error = styled.div`
  display: flex;
  align-items: baseline;
  text-align: left;
  font-size: 0.75rem;
  margin-top: 0.5rem;
  color: ${themeGet('colors.errorRed')};
`

export const ErrorText = styled.span`
  line-height: 0.9375rem;
  font-family: ${themeGet('font.molde.medium')};
`

export const NumberError = styled(Error)``

export const StyledAdminTooltip = styled(AdminTooltip)`
  ${TooltipContentWrapper} {
    top: -206px;
    left: -200px;
    transform: none;

    :: after {
      top: 140px;
      right: 30px;
      left: unset;
    }
  }
`

export const StyledCheckIcon = styled(CheckIconOrSpan)`
  width: 14px;
  height: 14px;
  color: ${themeGet('colors.white')};
`

export const StyledHr = styled.hr`
  height: 2px;
  width: 100%;
  border: none;
  margin-top: 1.25rem;
  background-color: ${themeGet('colors.white')};
`

export const StyledCalendarInput = styled(CalendarInputOrInput)
  .withConfig({ shouldForwardProp: prop => prop !== 'usePlaceholderColor' })
  .attrs(({ disabled }) => ({
    opacity: disabled ? 0.5 : 1,
  }))`
  height: 42px;
  border-color: ${themeGet('colors.blueGrayMedium')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  --calendar-input-text-color: ${props =>
    props.usePlaceholderColor
      ? themeGet('colors.gray1150')(props)
      : themeGet('colors.navyBlue')(props)};
  color: var(--calendar-input-text-color);

  .react-date-picker__calendar-button span {
    color: var(--calendar-input-text-color);
  }

  .react-date-picker__calendar .react-calendar {
    height: 20rem;
  }

  .react-date-picker--disabled {
    height: 100%;
    background-color: ${themeGet('colors.white')};
  }
`

export const StyledFormInputNumber = styled(FormInputNumberOrInput).withConfig({
  shouldForwardProp: prop => prop !== 'usePlaceholderColor',
})`
  color: ${props =>
    props.usePlaceholderColor
      ? themeGet('colors.gray1150')(props)
      : themeGet('colors.navyBlue')(props)};

  input {
    height: 46px;
    border-color: ${themeGet('colors.blueGrayMedium')};
    color: inherit;
  }

  [class*='FormFieldStyled-sc'] {
    margin-bottom: 0;
  }
`

export const StyledWarningIcon = styled(WarningIconOrSpan)`
  height: 9px;
  width: 9px;
  margin-right: 0.25rem;

  circle:first-child {
    fill: ${themeGet('colors.errorRed')};
  }
`
